import { Outlet, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom'
import { Box, Flex, VStack, HStack, Heading, Button, Container, Text } from '@chakra-ui/react'
import {
  Home,
  FileText,
  Calendar,
  MessageSquare,
  Bell,
  LogOut,
} from 'lucide-react'
import { Avatar } from '@/shared/components/Avatar'
import { LanguageSelector } from '@/shared/components/LanguageSelector'
import { useAuth, useLogout } from '@/features/auth/hooks/useAuth'
import { AgencyLogo } from '@/features/agencies/components/AgencyLogo'

export function ExtranetLayout() {
  const { data: auth } = useAuth()
  const logout = useLogout()
  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/app/extranet/login'),
    })
  }

  const navigationItems = [
    { path: '/app/extranet/dashboard', label: 'Accueil', icon: Home },
    { path: '/app/extranet/documents', label: 'Mes documents', icon: FileText },
    { path: '/app/extranet/meetings', label: 'Assemblées', icon: Calendar },
    { path: '/app/extranet/messages', label: 'Messages', icon: MessageSquare },
  ]

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  return (
    <Flex minH="100vh" bg="gray.50" direction="column">
      {/* Top Navigation Bar */}
      <Box
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
        position="sticky"
        top={0}
        zIndex={100}
      >
        <Container maxW="container.xl">
          <Flex h="70px" align="center" justify="space-between">
            {/* Logo */}
            <Flex align="center" gap={3}>
              <AgencyLogo variant="compact" height="45px" />
              <Box>
                <Heading size="md" fontWeight="bold" color="gray.900">
                  Espace Copropriétaire
                </Heading>
              </Box>
            </Flex>

            {/* Right actions */}
            <HStack gap={4}>
              {/* Notifications */}
              <Button
                variant="ghost"
                size="sm"
                p={2}
                borderRadius="lg"
                position="relative"
                _hover={{ bg: 'gray.100' }}
              >
                <Bell size={20} />
                <Box
                  position="absolute"
                  top={1}
                  right={1}
                  w={2}
                  h={2}
                  bg="red.500"
                  borderRadius="full"
                />
              </Button>

              {/* Profile Menu */}
              <Flex align="center" gap={2} cursor="pointer" px={2} py={1} borderRadius="lg" _hover={{ bg: 'gray.100' }}>
                <Avatar
                  size="sm"
                  name={
                    auth?.profile?.first_name && auth?.profile?.last_name
                      ? `${auth.profile.first_name} ${auth.profile.last_name}`
                      : auth?.user?.email || 'User'
                  }
                  bg="brand.500"
                />
                <VStack gap={0} align="start" display={{ base: 'none', md: 'flex' }}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.900">
                    {auth?.profile?.first_name && auth?.profile?.last_name
                      ? `${auth.profile.first_name} ${auth.profile.last_name}`
                      : 'Mon compte'}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Copropriétaire
                  </Text>
                </VStack>
              </Flex>

              {/* Logout */}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleLogout}
                loading={logout.isPending}
                colorScheme="red"
              >
                <LogOut size={18} />
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Flex flex={1}>
        <Container maxW="container.xl" py={6}>
          <Flex gap={6}>
            {/* Sidebar Navigation */}
            <Box
              w="240px"
              display={{ base: 'none', md: 'block' }}
              position="sticky"
              top="86px"
              h="fit-content"
            >
              <VStack align="stretch" gap={1}>
                {navigationItems.map((item) => {
                  const isActive = isActiveRoute(item.path)
                  const IconComponent = item.icon
                  return (
                    <RouterLink key={item.path} to={item.path}>
                      <Button
                        variant="ghost"
                        w="full"
                        justifyContent="flex-start"
                        px={4}
                        py={6}
                        borderRadius="lg"
                        fontWeight={isActive ? 'semibold' : 'normal'}
                        bg={isActive ? 'brand.50' : 'transparent'}
                        color={isActive ? 'brand.600' : 'gray.700'}
                        _hover={{
                          bg: isActive ? 'brand.100' : 'gray.100',
                        }}
                        transition="all 0.2s"
                      >
                        <HStack gap={3}>
                          <IconComponent size={20} />
                          <Text>{item.label}</Text>
                        </HStack>
                      </Button>
                    </RouterLink>
                  )
                })}
              </VStack>

              {/* Info Card */}
              <Box
                mt={6}
                p={4}
                bg="brand.50"
                borderRadius="xl"
                border="1px solid"
                borderColor="brand.100"
              >
                <VStack align="start" gap={2}>
                  <Heading size="sm" color="brand.700">
                    Besoin d'aide ?
                  </Heading>
                  <Text fontSize="sm" color="gray.700">
                    Contactez votre syndic pour toute question
                  </Text>
                  <Button size="sm" colorPalette="brand" variant="outline" w="full" mt={2}>
                    Nous contacter
                  </Button>
                </VStack>
              </Box>

              {/* Language Selector */}
              <Box mt={4}>
                <LanguageSelector />
              </Box>
            </Box>

            {/* Page Content */}
            <Box flex={1}>
              <Outlet />
            </Box>
          </Flex>
        </Container>
      </Flex>
    </Flex>
  )
}
